import { fabric } from 'fabric';
import ControlBase from '../fabric/ControlBase';
import { renderDragControl, renderConnectControl } from './controlRendering';
import { constrainToAxis } from '../utils/geometry';
import { type IPoint, type Point, type Transform } from 'fabric/fabric-impl';
import { AttachmentPoint, ConnectorEnd } from '../types';
import type Connector from '../objects/Connector';
import { type Attachable } from '../objects/Connector';
import RouterOrthogonal from '../objects/connectorRouters/RouterOrthogonal';
import type WhiteboardController from '../controllers/WhiteboardController';
import type Canvas from '../objects/Canvas';
import { ConnectorRouting } from '../objects/ConnectorRouting';
import {
  fetchAttachmentPoints,
  calculateEdgeAttachmentFromPosition,
  calculateEdgeAttachmentFromOffset,
  closestAttachablePoint,
  maybeWithinThreshold,
  pointWithinObject,
  allowsCenterAttachment,
} from '../utils/connectorAttachment';
import {
  hasConnectorAttachmentBehaviours,
  isSimpleGroup,
} from '../objects/guards';

/** Distance threshold for snapping to fixed attachment points (blue dots) */
const ATTACHMENT_SNAP_RADIUS = 25;

/** Maximum distance from shape edge for connector to snap to shape */
const EDGE_THRESHOLD = 50;

/** Control handle for a draggable point on a connector. */
class ConnectorPointControl extends ControlBase {
  /** Index of this point on the connector. */
  private pointIndex: number;
  /** The total number of points on the connector. */
  private readonly numPoints: number;
  /** Which end of the connector this point represents (if any). */
  private readonly whichEnd: ConnectorEnd | null;
  /** During a drag operation, which object this control is over or near. */
  private overObject: Attachable | null | undefined;
  /** whether the control is actually contained within overObject */
  private isContained?: boolean;

  /** During a drag operation, which attachment point this control is over. */
  private overAttachmentPoint: AttachmentPoint | null;

  /** During a drag operation, edge offset for edge attachments. */
  private overEdgeOffset?: number;

  actionName = 'dragPoint';
  minDimension = 0;

  constructor(connector: Connector, index: number, numPoints: number) {
    super({});
    let anchorIndex: number;
    if (index > 0) {
      if (
        connector.lineRouting === ConnectorRouting.ROUTING_ORTHOGONAL_MANUAL
      ) {
        anchorIndex = 0;
      } else {
        anchorIndex = index - 1;
      }
    } else {
      anchorIndex = numPoints - 1;
    }
    this.actionHandler = this.anchorWrapper(
      anchorIndex,
      this.anchoredActionHandler
    );
    this.pointIndex = index;
    this.numPoints = numPoints;
    if (index === 0) this.whichEnd = ConnectorEnd.CONNECTOR_START;
    else if (index === numPoints - 1)
      this.whichEnd = ConnectorEnd.CONNECTOR_END;
    else this.whichEnd = null;

    // Object that the dragged end of the connector is over.
    this.overObject = null;
  }

  getMouseDownHandler(
    eventData: MouseEvent,
    fabricObject: Connector,
    control: ConnectorPointControl
  ) {
    return () => {
      const connector = fabricObject;
      connector.controller.actionHideObjectToolbar();
      connector.beingControlled = true;
      control.overObject = null;
      control.overAttachmentPoint = null;
      control.overEdgeOffset = undefined;
      return false;
    };
  }

  getMouseUpHandler(
    eventData: MouseEvent,
    fabricObject: Connector,
    control: ConnectorPointControl
  ) {
    return () => {
      const connector = fabricObject;
      connector.canvas.controller.actionResetStylus();
      connector.canvas.controller.actionShowObjectToolbar();
      // Handles undo for split control as well
      connector.canvas.controller.undoManager.completeUndoGroup();
      control.overObject = null;
      control.overAttachmentPoint = null;
      control.overEdgeOffset = undefined;
      connector.fire('movedPoint');
      connector.beingControlled = false;

      // We may have flipped the orientation of an orthogonal line, so recompute
      // the controls.
      connector.createControls();

      return false;
    };
  }

  // Position the control handle.
  positionHandler(dim: IPoint, finalMatrix: any, fabricObject: Connector) {
    const x =
      fabricObject.points[this.pointIndex].x - fabricObject.pathOffset.x;
    const y =
      fabricObject.points[this.pointIndex].y - fabricObject.pathOffset.y;

    return fabric.util.transformPoint(
      { x, y } as Point,
      fabric.util.multiplyTransformMatrices(
        fabricObject.canvas.viewportTransform!,
        fabricObject.calcTransformMatrix(true)
      )
    );
  }

  // TODO: Can we memoize this during a drag.
  _findShapeTargets(
    canvas: Canvas,
    pointer: IPoint,
    connector: Connector,
    ignoreTargets: boolean
  ) {
    if (ignoreTargets) return { obj: null };

    const otherEndObject =
      this.whichEnd == null
        ? null
        : connector.attachments[
            (connector.constructor as typeof Connector).otherEnd(this.whichEnd)
          ]?.object;

    const threshold = EDGE_THRESHOLD / canvas.getZoom();

    // Assume canvas.getObjects() gives objects sorted correctly by z
    // iterate from top down so we find the topmost first
    let found: Attachable | undefined;
    let isContained = false;
    let edgeDist: number = threshold;
    const depthLimit = 10;

    const searchObjects = (objects: fabric.Object[], depth = 0) => {
      if (depth > depthLimit) return;

      for (const obj of objects.toReversed()) {
        // skip obvious non-targets
        if (
          obj === otherEndObject ||
          obj.connectorId === connector.id || // don't connect to own labels
          !obj.visible ||
          (!hasConnectorAttachmentBehaviours(obj) && !isSimpleGroup(obj)) ||
          !obj.isOnScreen()
        ) {
          continue;
        }

        // skip if definitely not within threshold (note this checks against the overall bounding
        // box of groups as well as connectable shapes)
        if (!maybeWithinThreshold(obj, pointer as Point, edgeDist)) {
          continue;
        }

        if (isSimpleGroup(obj)) {
          searchObjects(obj.getObjects(), depth + 1);

          if (found && isContained) {
            // the pointer must be contained within a shape, so stop iterating and recursing here
            break;
          } else {
            // otherwise, continue in the loop to see if we can find a closer shape
            continue;
          }
        }

        // if actually over a shape that takes precedence, but only if it supports center attachment
        if (
          allowsCenterAttachment(obj) &&
          pointWithinObject(obj, pointer as Point)
        ) {
          found = obj;
          isContained = true;
          break;
        }

        // otherwise, find the shape with the closest edge within threshold
        const edge = calculateEdgeAttachmentFromPosition(
          obj,
          pointer as Point,
          edgeDist
        );
        if (edge) {
          edgeDist = edge.distance;
          found = obj;
        }
      }
    };

    searchObjects(canvas.getObjects());

    return { obj: found ?? null, isContained };
  }

  /**
   * Snaps connector to a give attachment point while the user is dragging over
   * a shape. If the user attaches to center, find the closest attachmetn point
   * on the shape to the origin of the connector
   */
  _snapToAttachmentPoint(
    canvas: Canvas,
    connector: Connector,
    position: Point
  ) {
    if (!this.overObject || this.whichEnd === null) return;

    const attachmentThreshold = ATTACHMENT_SNAP_RADIUS / canvas.getZoom();

    // find the closest fixed attachment point, if any are within threshold
    const attachments = fetchAttachmentPoints(this.overObject, {
      withStandoff: true,
    });

    let closestAttachment = closestAttachablePoint(
      attachments,
      position,
      attachmentThreshold
    );

    // if we didn't find a fixed attachment point, find closest edge attachment
    const edgeAttachment = !closestAttachment
      ? calculateEdgeAttachmentFromPosition(
          this.overObject,
          position,
          // if pointer is inside shape, use a threshold so user isn't forced onto an attachment point
          // if outside shape, find the closest point regardless of distance
          this.isContained ? attachmentThreshold : undefined
        )
      : undefined;

    // if we have an edge attachment, check if it is close to a fixed point
    if (edgeAttachment) {
      closestAttachment = closestAttachablePoint(
        attachments,
        edgeAttachment.point,
        attachmentThreshold
      );
    }

    // If we have neither a fixed nor edge attachment, and the shape allows it, attach to the center
    if (
      !closestAttachment &&
      !edgeAttachment &&
      allowsCenterAttachment(this.overObject)
    ) {
      const position = this.overObject.positionForConnectorAttachment(
        AttachmentPoint.Center,
        connector,
        this.whichEnd
      );

      if (position) {
        closestAttachment = {
          position,
          attachmentPoint: AttachmentPoint.Center,
        };
      }
    }

    // finally, if we found any attachment point, snap to it
    if (closestAttachment) {
      this.overAttachmentPoint = closestAttachment.attachmentPoint;
      position.x = closestAttachment.position.x;
      position.y = closestAttachment.position.y;
    } else if (edgeAttachment) {
      this.overAttachmentPoint = AttachmentPoint.Edge;
      this.overEdgeOffset = edgeAttachment.offset;
      position.x = edgeAttachment.point.x;
      position.y = edgeAttachment.point.y;
    } else {
      this.overAttachmentPoint = null;
      this.overEdgeOffset = undefined;
    }
  }

  // Dynamically attach or detach the connector from objects during the
  // drag so that that the connector rendering reflects the attachment.
  _attachDetachDuringDrag(
    connector: Connector,
    control: ConnectorPointControl
  ) {
    if (control.overObject && control.overAttachmentPoint) {
      const whichEnd =
        control.pointIndex === 0
          ? ConnectorEnd.CONNECTOR_START
          : ConnectorEnd.CONNECTOR_END;
      control.overObject.attachConnector(
        connector,
        whichEnd,
        control.overAttachmentPoint,
        control.overEdgeOffset
      );
    } else {
      // Detach the connector from any object it was connected to.
      connector.detachObjectAtIndex(control.pointIndex);
    }
  }

  // Find which point to constrain drawing relative to - either the previous
  // point in most cases, or the next point when moving the first point.
  constrainRelativePoint(points: Point[], relativePointIndex: number): Point {
    if (relativePointIndex === 0) {
      return points[relativePointIndex + 1];
    } else {
      return points[relativePointIndex - 1];
    }
  }

  // Handle mouse movements while dragging the control.
  anchoredActionHandler(
    eventData: MouseEvent,
    transform: Transform,
    x: number,
    y: number
  ): boolean {
    const connector = transform.target as Connector;
    const currentControl = connector.controls[
      connector.__corner as string
    ] as ConnectorPointControl;
    const pointer = { x, y };

    if (!currentControl) {
      return false;
    }

    if (!transform.constraining) {
      // If we are dragging an end of the connector, see if it is over an
      // object that can be attached to. If so, constrain the pointer to
      // one of the attachment points.
      if (currentControl.whichEnd !== null) {
        const canvas = transform.target.canvas;
        const target = currentControl._findShapeTargets(
          canvas,
          pointer,
          connector,
          eventData.ctrlKey || eventData.metaKey
        );
        currentControl.overObject = target.obj;
        currentControl.isContained = target.isContained;

        // If we are over an object, snap to the closest attachment point
        if (currentControl.overObject) {
          currentControl._snapToAttachmentPoint(
            canvas,
            connector,
            pointer as Point
          );
        } else {
          currentControl.overAttachmentPoint = null;
          currentControl.overEdgeOffset = undefined;
        }
      } else {
        currentControl.overObject = null;
        currentControl.overAttachmentPoint = null;
        currentControl.overEdgeOffset = undefined;
      }

      currentControl._attachDetachDuringDrag(connector, currentControl);
    }

    // Position the connector point.
    const mouseLocalPosition = fabric.util.transformPoint(
      pointer as Point,
      fabric.util.invertTransform(connector.calcTransformMatrix())
    );
    const connectorBaseSize = connector._getNonTransformedDimensions();
    const size = connector._getTransformedDimensions(0, 0);
    let finalPointPosition = {
      x:
        (mouseLocalPosition.x * connectorBaseSize.x) / size.x +
        connector.pathOffset.x,
      y:
        (mouseLocalPosition.y * connectorBaseSize.y) / size.y +
        connector.pathOffset.y,
    };

    // If the user is holding down the shift key, then constrain movement
    // to a standard axis.
    if (eventData?.shiftKey && !currentControl.overAttachmentPoint) {
      finalPointPosition = constrainToAxis(
        finalPointPosition,
        currentControl.constrainRelativePoint(
          connector.points as Point[],
          currentControl.pointIndex
        )
      );
    }

    // Constrain orthogonal lines.
    if (connector.lineRouting === ConnectorRouting.ROUTING_ORTHOGONAL_MANUAL) {
      const nextPoint =
        currentControl.pointIndex === 0
          ? connector.points[1]
          : connector.points[connector.points.length - 2];
      const afterIndex =
        currentControl.pointIndex === 0 ? 0 : connector.points.length - 2;

      if (RouterOrthogonal.segmentHorizontal(connector, afterIndex)) {
        nextPoint.y = finalPointPosition.y;
      } else {
        nextPoint.x = finalPointPosition.x;
      }
    }

    const newPoints = [...connector.points];
    newPoints[currentControl.pointIndex] = finalPointPosition;
    connector.set('points', newPoints);
    connector._setPositionDimensions();

    return true;
  }

  // Keep the overall object in same position as the handles move.
  anchorWrapper(
    anchorIndex: number,
    fn: (
      eventData: MouseEvent,
      transform: Transform,
      x: number,
      y: number
    ) => boolean
  ) {
    return function (
      eventData: MouseEvent,
      transform: Transform,
      x: number,
      y: number
    ) {
      const fabricObject = transform.target as Connector;
      const absolutePoint = fabric.util.transformPoint(
        {
          x: fabricObject.points[anchorIndex].x - fabricObject.pathOffset.x,
          y: fabricObject.points[anchorIndex].y - fabricObject.pathOffset.y,
        } as Point,
        fabricObject.calcTransformMatrix(true)
      );
      const actionPerformed = fn(eventData, transform, x, y);
      const connectorBaseSize = fabricObject._getNonTransformedDimensions();
      const newX =
        (fabricObject.points[anchorIndex].x - fabricObject.pathOffset.x) /
        connectorBaseSize.x;
      const newY =
        (fabricObject.points[anchorIndex].y - fabricObject.pathOffset.y) /
        connectorBaseSize.y;

      // @ts-expect-error types expect center/left/right string, but function seems to handle absolute values too
      fabricObject.setPositionByOrigin(absolutePoint, newX + 0.5, newY + 0.5);
      return actionPerformed;
    };
  }

  render(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    styleOverride: unknown,
    fabricObject: Connector
  ) {
    if (!fabricObject.beingControlled) {
      // Render point we are dragging.
      renderDragControl.call(this, ctx, left, top, styleOverride, fabricObject);
    }

    if (this.overObject) {
      ctx.save();
      const v = fabricObject.canvas.viewportTransform!;
      ctx.transform(v[0], v[1], v[2], v[3], v[4], v[5]);

      // Render drop targets on the object we are over.
      const indicators = fetchAttachmentPoints(this.overObject, {
        withStandoff: false,
      }).map(attachment => {
        return {
          position: attachment.position,
          active: attachment.attachmentPoint === this.overAttachmentPoint,
        };
      });

      // Render blue dot at edge attachment point if attaching to edge
      if (
        this.overAttachmentPoint === AttachmentPoint.Edge &&
        this.overEdgeOffset !== undefined
      ) {
        const edgeAttachment = calculateEdgeAttachmentFromOffset(
          this.overObject,
          this.overEdgeOffset
        );
        if (edgeAttachment) {
          indicators.push({
            position: edgeAttachment.indicator,
            active: true,
          });
        }
      }

      indicators.forEach(({ position, active }) => {
        renderConnectControl.call(
          this.overObject,
          ctx,
          position.x,
          position.y,
          active
        );
      });

      // Highlight shape if attaching to center
      if (this.overAttachmentPoint === AttachmentPoint.Center) {
        this.overObject.transform(ctx);
        this.overObject.renderHighlight?.(ctx, { level: 1, stroke: false });
      }
      ctx.restore();
    }
  }

  showRightClickMenu(
    controller: WhiteboardController,
    fabricObject: Connector,
    x: number,
    y: number
  ) {
    if (this.whichEnd === null) {
      controller.actionShowConnectorPointMenu({
        x,
        y,
        object: fabricObject,
        pointIndex: this.pointIndex,
      });
    }
  }
}

export default ConnectorPointControl;
